import Button from '@brands/Components/Button/Button';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import MinimalDateRangePicker from '@brands/Components/MinimalDateRangePicker/MinimalDateRangePicker';
import useAuditTrail from '@brands/hooks/useAuditTrail';
import useDebounce from '@brands/hooks/useDebounce';
import { usePersistedRowsPerPage } from '@brands/hooks/usePersistedRowsPerPage';
import { AuditEvent } from '@brands/services/audit_trails/listAuditTrails';
import { TablePaginationKey } from '@brands/store/slices/tablePaginationSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { formatDateParts } from '@brands/Utils/formatDate';
import { triggerAuditTrailCsvDownload } from '@brands/Utils/triggerAuditTrailCsvDownload';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Avatar, Box, FormControl, MenuItem, Select, TextField } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { HiChevronLeft } from 'react-icons/hi';

import CADataGrid, { caGridStyles, ColumnDef } from '../../../Dashboard/CareAdvisor/Components/CADataGrid';
import SelectClearButton from '../../Components/SelectClearButton';
import styles from './styles.module.scss';

export type CaseActivityContentProps = {
  caseId: number;
  userRole?: string;
  onBack: () => void;
};

const filterLabelSx = { fontSize: 12, fontWeight: 400 };

const SORT_KEY_MAP: Record<string, 'created_at' | 'actor_name' | 'action'> = {
  actor: 'actor_name',
  action_message: 'action',
  created_at: 'created_at',
};

const getActionSelectSx = (showClearButton: boolean) => ({
  '& .MuiSelect-select': {
    paddingRight: showClearButton ? '56px !important' : undefined,
  },
});

const getInitials = (name?: string): string => {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const CaseActivityContent = ({ caseId, userRole, onBack }: CaseActivityContentProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState<[Date, Date] | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = usePersistedRowsPerPage(TablePaginationKey.CaseActivity, 10);
  const [sortColKey, setSortColKey] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const formatDateToLocalString = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const handleDateRangeFilterChange = useCallback((_key: string, value: [Date, Date] | null): void => {
    setSelectedDateRange(value);
    setPage(0);
  }, []);

  const debouncedNameFilter = useDebounce(nameFilter, 500);
  const debouncedActionFilter = useDebounce(actionFilter, 500);

  const params = useMemo(
    () => ({
      case_id: caseId,
      sort_by: SORT_KEY_MAP[sortColKey] ?? 'created_at',
      sort_direction: sortDirection,
      limit: rowsPerPage,
      ...(debouncedNameFilter ? { actor_name: debouncedNameFilter } : {}),
      ...(debouncedActionFilter ? { action_message: debouncedActionFilter } : {}),
      ...(selectedDateRange
        ? {
            date_from: formatDateToLocalString(selectedDateRange[0]),
            date_to: formatDateToLocalString(selectedDateRange[1]),
          }
        : {}),
    }),
    [caseId, debouncedNameFilter, debouncedActionFilter, selectedDateRange, sortColKey, sortDirection, rowsPerPage],
  );

  const {
    auditTrailList,
    loadNextPage,
    loadPreviousPage,
    dataLoading,
    loading,
    totalRecords,
  } = useAuditTrail(params, userRole, { paginationMode: 'page' });

  const handleSort = useCallback(
    (key: string) => {
      if (!SORT_KEY_MAP[key]) return;
      setPage(0);
      if (sortColKey === key) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColKey(key);
        setSortDirection('asc');
      }
    },
    [sortColKey],
  );

  const handlePageChange = useCallback(
    async (nextPage: number): Promise<void> => {
      if (nextPage > page) {
        await loadNextPage();
      } else if (nextPage < page) {
        await loadPreviousPage();
      }
      setPage(nextPage);
    },
    [loadNextPage, loadPreviousPage, page],
  );

  const handleDownloadClick = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await triggerAuditTrailCsvDownload(params, userRole);
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setIsLoading(false);
    }
  };

  const actionOptions = useMemo(() => {
    const uniqueActions = [...new Set(auditTrailList.map((item) => item.action_message).filter(Boolean))];
    return uniqueActions.sort();
  }, [auditTrailList]);

  const columns: ColumnDef<AuditEvent>[] = useMemo(
    () => [
      {
        key: 'actor',
        label: 'Name',
        widthPx: 280,
        renderCell: (record) => {
          if (!record.actor) {
            return <span className={`fs-exclude ${caGridStyles.cellText}`}>N/A</span>;
          }
          return (
            <Box className="fs-exclude" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 14,
                  bgcolor: '#bdbdbd',
                  flexShrink: 0,
                }}
              >
                {getInitials(record.actor.full_name)}
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.43, minWidth: 0 }}>
                <span className={caGridStyles.cellText}>{record.actor.full_name}</span>
                {record.actor.role?.name && (
                  <span style={{ fontSize: 14, color: 'rgba(23, 36, 51, 0.6)' }}>({record.actor.role.name})</span>
                )}
              </Box>
            </Box>
          );
        },
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Name"
            value={nameFilter}
            onChange={(e) => {
              setNameFilter(e.target.value);
              setPage(0);
            }}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(nameFilter), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'action_message',
        label: 'Action',
        widthPx: 328,
        renderCell: (record) => (
          <span className={`fs-exclude ${caGridStyles.cellText}`}>{record.action_message}</span>
        ),
        filterContent: (
          <FormControl variant="standard" fullWidth sx={{ position: 'relative' }}>
            <Select
              value={actionFilter}
              displayEmpty
              disableUnderline
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                fontSize: 13,
                px: 0.5,
                ...getActionSelectSx(Boolean(actionFilter)),
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Box component="span" sx={{ color: 'rgba(13, 32, 53, 0.6)', fontSize: 12 }}>
                      Action
                    </Box>
                  );
                }
                return selected;
              }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {actionOptions.map((action) => (
                <MenuItem key={action} value={action}>
                  {action}
                </MenuItem>
              ))}
            </Select>
            <SelectClearButton
              show={Boolean(actionFilter)}
              label="action filter"
              onClear={() => {
                setActionFilter('');
                setPage(0);
              }}
            />
          </FormControl>
        ),
      },
      {
        key: 'created_at',
        label: 'Modification date',
        widthPx: 231,
        renderCell: (record) => {
          if (!record.created_at) {
            return <span className={`fs-unmask ${caGridStyles.cellText}`}>N/A</span>;
          }
          const parts = formatDateParts(record.created_at);
          return (
            <span
              className={`fs-unmask ${caGridStyles.cellText}`}
              style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.43 }}
            >
              <span>{parts.date}</span>
              <span style={{ color: 'rgba(23, 36, 51, 0.6)' }}>{parts.time}</span>
            </span>
          );
        },
        filterContent: (
          <MinimalDateRangePicker
            handleDateRangeFilterChange={handleDateRangeFilterChange}
            filterKey="case_activity"
            selectedRange={selectedDateRange}
            width="100%"
          />
        ),
      },
    ],
    [actionFilter, actionOptions, handleDateRangeFilterChange, nameFilter, selectedDateRange],
  );

  return (
    <div className={styles.content}>
      <button type="button" className={styles.backButton} onClick={onBack}>
        <HiChevronLeft />
        Back
      </button>
      <div className={styles.headerRow}>
        <h2 className={styles.pageTitle}>Case Activity</h2>
        <Button onClick={handleDownloadClick} disabled={isLoading} className={styles.downloadCSVBtn}>
          <SaveAltIcon fontSize="small" />
          Download CSV
        </Button>
      </div>
      <div className={styles.ccDashboardBody}>
        {dataLoading && auditTrailList.length === 0 ? (
          <Loading dotted />
        ) : (
          <div className={styles.gridWrapper}>
            <CADataGrid
              columns={columns}
              rows={auditTrailList}
              getRowId={(row) => row.id}
              count={totalRecords}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 100]}
              onPageChange={(p) => {
                void handlePageChange(p);
              }}
              onRowsPerPageChange={(rpp) => {
                setRowsPerPage(rpp);
                setPage(0);
              }}
              loading={dataLoading || loading}
              sortBy={sortColKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              showManageColumns={false}
              emptyMessage="No data to display"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseActivityContent;
